import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2, Search, Eye, EyeOff, Upload, Download, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPackages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publicationFilter, setPublicationFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchPublications();
    fetchPackages();
  }, [publicationFilter]);

  const fetchPublications = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/publications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublications(response.data || []);
    } catch (error) {
      console.error('Error fetching publications:', error);
    }
  };

  const fetchPackages = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (publicationFilter) params.append('publication_id', publicationFilter);
      
      const response = await axios.get(`${API}/admin/packages?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const packagesData = response.data || [];
      setPackages(packagesData);
      setTotal(packagesData.length);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}" package?`)) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  const downloadTemplate = () => {
    const template = 'publication_name,name,price,tat_days_min,tat_days_max,word_count_min,word_count_max,links_included,link_type,revisions\nThe Economic Times,Express,52500,3,5,400,600,1,dofollow,1\nThe Economic Times,Standard,35000,5,7,600,800,2,dofollow,2\nYourStory,Premium,25000,5,7,800,1200,3,dofollow,3';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'packages_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      if (row.name && row.publication_name) data.push(row);
    }
    return data;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const data = parseCSV(text);
      setBulkData(data);
      setShowBulkUpload(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBulkUpload = async () => {
    if (bulkData.length === 0) return;
    
    setUploading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/admin/packages/bulk-upload`, bulkData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Created ${response.data.created} packages`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
        console.log('Bulk upload errors:', response.data.errors);
      }
      
      setShowBulkUpload(false);
      setBulkData([]);
      fetchPackages();
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error('Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="font-playfair text-4xl font-bold mb-2">Packages</h1>
          <p className="font-manrope text-sm text-gray-600">Manage pricing packages for publications</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 font-manrope text-xs uppercase tracking-widest hover:bg-gray-50 transition-all duration-300"
          >
            <Download className="w-4 h-4" />
            Template
          </button>
          <label className="flex items-center gap-2 px-4 py-3 border border-[#D4AF37] text-[#D4AF37] font-manrope text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer">
            <Upload className="w-4 h-4" />
            Bulk Upload
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              ref={fileInputRef}
              className="hidden"
            />
          </label>
          <Link
            to="/admin/packages/create"
            data-testid="create-package-button"
            className="flex items-center gap-2 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
          >
            <Plus className="w-4 h-4" />
            Add Package
          </Link>
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="font-playfair text-2xl font-bold">Preview Bulk Upload</h2>
              <button onClick={() => { setShowBulkUpload(false); setBulkData([]); }}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <p className="font-manrope text-sm text-gray-600 mb-4">{bulkData.length} packages to import</p>
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2">Publication</th>
                    <th className="text-left p-2">Package Name</th>
                    <th className="text-left p-2">Price</th>
                    <th className="text-left p-2">TAT</th>
                    <th className="text-left p-2">Word Count</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkData.map((row, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2">{row.publication_name}</td>
                      <td className="p-2 font-semibold">{row.name}</td>
                      <td className="p-2">₹{Number(row.price).toLocaleString()}</td>
                      <td className="p-2">{row.tat_days_min}-{row.tat_days_max} days</td>
                      <td className="p-2">{row.word_count_min}-{row.word_count_max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowBulkUpload(false); setBulkData([]); }}
                className="px-6 py-2 border border-gray-300 font-manrope text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={uploading}
                className="px-6 py-2 bg-[#D4AF37] text-white font-manrope text-sm hover:bg-[#B8960F] disabled:opacity-50"
              >
                {uploading ? 'Importing...' : `Import ${bulkData.length} Packages`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 border border-gray-200 p-6 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Search Packages</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by package name, publication..."
                data-testid="packages-search-input"
                className="w-full border border-gray-200 pl-10 pr-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Filter by Publication</label>
            <select
              value={publicationFilter}
              onChange={(e) => setPublicationFilter(e.target.value)}
              data-testid="publication-filter-select"
              className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
            >
              <option value="">All Publications</option>
              {publications.map(pub => (
                <option key={pub.id} value={pub.id}>{pub.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {(() => {
        const filteredPackages = packages.filter(pkg => {
          if (!searchQuery) return true;
          const query = searchQuery.toLowerCase();
          const pubName = publications.find(p => p.id === pkg.publication_id)?.name || '';
          return (
            pkg.name.toLowerCase().includes(query) ||
            pubName.toLowerCase().includes(query)
          );
        });

        return loading ? (
          <div className="text-center py-12">
            <p className="font-manrope text-gray-600">Loading...</p>
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="text-center py-12 border border-gray-200 p-12">
            <p className="font-manrope text-gray-600 mb-4">
              {searchQuery ? 'No packages match your search' : 'No packages found'}
            </p>
            {!searchQuery && (
              <Link
                to="/admin/packages/create"
                className="inline-block px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
              >
                Create First Package
              </Link>
            )}
          </div>
        ) : (
          <>
            <p className="font-manrope text-sm text-gray-600 mb-4">
              Showing {filteredPackages.length}{searchQuery && ` of ${packages.length}`} packages
            </p>
          
          <div className="border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Package</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Publication</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Price</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">TAT</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Status</th>
                  <th className="text-right px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPackages.map((pkg) => (
                  <tr key={pkg.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`package-row-${pkg.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-manrope font-semibold text-sm">{pkg.name}</div>
                      <div className="font-manrope text-xs text-gray-500">
                        {pkg.word_count_min}-{pkg.word_count_max} words
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-manrope text-sm">{pkg.publication_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono font-semibold text-sm">₹{pkg.price.toLocaleString('en-IN')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-manrope text-sm">{pkg.tat_days_min}-{pkg.tat_days_max} days</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {pkg.is_active ? (
                          <>
                            <Eye className="w-4 h-4 text-green-600" />
                            <span className="font-manrope text-sm text-green-600">Active</span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 text-gray-400" />
                            <span className="font-manrope text-sm text-gray-400">Inactive</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/packages/edit/${pkg.id}`}
                          data-testid={`edit-package-${pkg.id}`}
                          className="p-2 border border-gray-200 hover:border-black transition-all duration-300"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(pkg.id, pkg.name)}
                          data-testid={`delete-package-${pkg.id}`}
                          className="p-2 border border-gray-200 hover:border-red-600 hover:text-red-600 transition-all duration-300"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
        );
      })()}
    </AdminLayout>
  );
};

export default AdminPackages;